import { Controller } from "@hotwired/stimulus"

// Story page reading chrome: the slim top bar that takes over once the title scrolls away, reading
// progress and time left, the drop cap, and focus mode (button or "f"; Esc leaves).
//
// Progress is measured over the story body, not the whole document, so the comments underneath
// don't make the bar crawl. Where CSS scroll-driven animations exist, the bar itself is drawn by
// the browser (see story.css) and this only updates the minutes-left label.
const DROPCAP_MIN_CHARS = 140

export default class extends Controller {
  static targets = ["progress", "topbar", "title", "minutesLeft", "body", "focusButton"]
  static values = { minutes: Number }

  connect() {
    this.cssDrivesProgress = window.CSS?.supports?.("animation-timeline: view()") ?? false
    this.lastLabel = null

    this.applyDropcap()

    this.onScroll = () => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => {
        this.frame = null
        this.updateProgress()
      })
    }
    window.addEventListener("scroll", this.onScroll, { passive: true })
    window.addEventListener("resize", this.onScroll, { passive: true })
    this.updateProgress()

    if (this.hasTitleTarget && this.hasTopbarTarget && "IntersectionObserver" in window) {
      this.titleObserver = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0
        this.topbarTarget.classList.toggle("is-visible", past)
      })
      this.titleObserver.observe(this.titleTarget)
    }

    this.onKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.onKeydown)
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("resize", this.onScroll)
    document.removeEventListener("keydown", this.onKeydown)
    if (this.frame) cancelAnimationFrame(this.frame)
    this.titleObserver?.disconnect()
    document.body.classList.remove("reader-focus")
  }

  // The dock is re-rendered by a turbo-stream after every like; keep its pressed state honest.
  focusButtonTargetConnected(button) {
    button.setAttribute("aria-pressed", String(document.body.classList.contains("reader-focus")))
  }

  toggle() {
    this.setFocus(!document.body.classList.contains("reader-focus"))
  }

  setFocus(on) {
    const apply = () => {
      document.body.classList.toggle("reader-focus", on)
      this.focusButtonTargets.forEach((b) => b.setAttribute("aria-pressed", String(on)))
    }
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (document.startViewTransition && !reduce) {
      // Keep the reader's place: anchor on the paragraph currently at the top of the screen.
      const anchor = this.paragraphAtTop()
      const offset = anchor?.getBoundingClientRect().top
      document.startViewTransition(() => {
        apply()
        if (anchor) window.scrollBy(0, anchor.getBoundingClientRect().top - offset)
      })
    } else {
      apply()
    }
  }

  handleKeydown(event) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return
    const el = event.target
    if (el.closest?.("input, textarea, select, [contenteditable=''], [contenteditable='true']")) return
    if (event.key === "f" || event.key === "F") {
      event.preventDefault()
      this.toggle()
    } else if (event.key === "Escape" && document.body.classList.contains("reader-focus")) {
      this.setFocus(false)
    }
  }

  applyDropcap() {
    if (!this.hasBodyTarget) return
    const first = this.bodyTarget.firstElementChild
    if (first?.tagName === "P" && first.textContent.trim().length >= DROPCAP_MIN_CHARS) {
      this.bodyTarget.classList.add("has-dropcap")
    }
  }

  paragraphAtTop() {
    if (!this.hasBodyTarget) return null
    for (const el of this.bodyTarget.children) {
      if (el.getBoundingClientRect().bottom > 0) return el
    }
    return null
  }

  updateProgress() {
    if (!this.hasBodyTarget) return
    const rect = this.bodyTarget.getBoundingClientRect()
    const total = rect.height - window.innerHeight
    const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : (rect.bottom <= window.innerHeight ? 1 : 0)

    if (!this.cssDrivesProgress && this.hasProgressTarget) {
      this.progressTarget.style.setProperty("--read-progress", progress.toFixed(4))
    }

    if (this.hasMinutesLeftTarget && this.minutesValue > 0) {
      const left = Math.ceil(this.minutesValue * (1 - progress))
      const label = progress >= 0.985 ? "Finished" : `${Math.max(1, left)} min left`
      if (label !== this.lastLabel) {
        this.minutesLeftTarget.textContent = label
        this.lastLabel = label
      }
    }
  }
}
